import React, { useEffect, useRef, useState } from 'react';
import { Loader, AlertCircle, ChevronLeft, ChevronRight, Play } from 'lucide-react';
import { SafeZoneOverlay } from './video/SafeZoneOverlay';
import { VideoControls } from './VideoControls';
import { processVideoUrl, getVideoErrorMessage, isSupabaseStorageUrl } from '../utils/video/player';

interface VideoPlayerProps {
  url: string;
  isDesktop: boolean;
  isPhotoPost?: boolean;
  imageUrls?: string[];
  // Optional poster image shown before the first frame is decoded
  // (prevents the "black before play" flash on autoplay-blocked browsers).
  poster?: string;
  onError?: (error: Error) => void;
  forwardedRef?: React.RefObject<HTMLVideoElement>;
  autoPlay?: boolean;
}

export const VideoPlayer: React.FC<VideoPlayerProps> = ({ url, isDesktop = false, isPhotoPost = false, imageUrls = [], poster, onError, forwardedRef, autoPlay = false }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [volumeLevel, setVolumeLevel] = useState(0.1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processedUrl, setProcessedUrl] = useState<string>('');
  const [showSafeZone, setShowSafeZone] = useState(false);
  const [aspectRatio, setAspectRatio] = useState<'9:16' | '16:9' | '1:1' | '4:5'>('9:16');
  // Raw video dimensions for the info display below the player
  const [videoDimensions, setVideoDimensions] = useState<{ width: number; height: number } | null>(null);
  // iOS Safari blocks autoplay with sound. We start muted so autoplay is
  // allowed, then the user can unmute via the controls.
  const [isMuted, setIsMuted] = useState(true);
  // iOS Safari does NOT reliably render the <video poster> attribute — it
  // often shows a black rectangle instead. We render an <img> overlay on
  // top of the video and only hide it once the video has actually started
  // playing (onPlaying), so the user always sees the thumbnail + play
  // button. Falls back to a dark background when no poster URL is given.
  const [showPoster, setShowPoster] = useState(true);
  // Tracks whether the video has ever played WITH sound. The big center
  // play button + poster overlay only show before the first sound playback
  // (initial load or muted autoplay). After that, pausing shows the frozen
  // video frame with the player bar (no big play button).
  const [hasPlayedWithSound, setHasPlayedWithSound] = useState(false);

  const videoRef = forwardedRef || useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const controlsRef = useRef<HTMLDivElement>(null);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    console.log('[VideoPlayer] URL changed, resetting state:', { url, poster, autoPlay });
    setCurrentTime(0);
    setDuration(0);
    setIsLoading(true);
    setError(null);
    setIsPlaying(false);
    setShowPoster(true);
    setHasPlayedWithSound(false);

    try {
      const processed = processVideoUrl(url);
      console.log('[VideoPlayer] Processed URL:', processed);
      setProcessedUrl(processed);
    } catch (err) {
      console.error('[VideoPlayer] Error processing video URL:', err);
      setError('Invalid video URL format. Please check the URL and try again.');
      setIsLoading(false);
    }
  }, [url]);

  // Set initial volume (runs once per video URL, not on every isLoading flip).
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = true;
      videoRef.current.volume = volumeLevel;
      setIsMuted(true);
      console.log('[VideoPlayer] Init: muted=true, volume=', volumeLevel);
    }
  }, [processedUrl]);

  // Attempt muted autoplay once the video is ready
  useEffect(() => {
    if (!autoPlay || isLoading || error || !processedUrl || !videoRef.current) return;

    const attemptAutoplay = async () => {
      try {
        if (!videoRef.current) return;
        // Ensure muted is set right before play() — iOS re-checks at
        // the moment play() is called.
        videoRef.current.muted = true;
        console.log('[VideoPlayer] Attempting muted autoplay...');
        await videoRef.current.play();
        console.log('[VideoPlayer] Muted autoplay succeeded');
        setIsPlaying(true);
      } catch (err) {
        console.error('[VideoPlayer] Autoplay failed:', err);
        setIsPlaying(false);
      }
    };

    console.log('[VideoPlayer] Autoplay conditions met, calling attemptAutoplay');
    attemptAutoplay();
  }, [isLoading, error, processedUrl, autoPlay]);

  const handleVideoClick = (e: React.MouseEvent) => {
    console.log('[VideoPlayer] handleVideoClick:', { isPlaying, isMuted, hasPlayedWithSound });
    if (controlsRef.current?.contains(e.target as Node)) {
      return;
    }
    // If video is playing muted (autoplay), tap unmutes (enables sound).
    // If not yet played with sound (initial), tap plays with sound.
    // After first sound playback, tap toggles play/pause (frozen frame
    // shows on pause, no big play button overlay).
    if (isPlaying && isMuted) {
      toggleMute();
    } else {
      togglePlayPause(e);
    }
  };

  const togglePlayPause = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!videoRef.current || error) return;

    console.log('[VideoPlayer] togglePlayPause: isPlaying=', isPlaying, 'hasPlayedWithSound=', hasPlayedWithSound);

    try {
      if (isPlaying) {
        console.log('[VideoPlayer] Pausing video...');
        await videoRef.current.pause();
        setIsPlaying(false);
      } else {
        // When the user manually presses play, unmute so they get sound.
        // This is a user gesture, so iOS allows playback with sound.
        console.log('[VideoPlayer] Playing with sound (user gesture), unmuting...');
        videoRef.current.muted = false;
        setIsMuted(false);
        setShowPoster(false);
        setHasPlayedWithSound(true);
        await videoRef.current.play();
        console.log('[VideoPlayer] Play with sound succeeded');
        setIsPlaying(true);
      }
    } catch (err) {
      console.error('[VideoPlayer] Video playback error:', err);
      setIsPlaying(false);
      setError('Failed to play video. Please try again.');
    }
  };

  const handleVolumeChange = (level: number) => {
    if (videoRef.current) {
      console.log('[VideoPlayer] handleVolumeChange:', level);
      videoRef.current.volume = level;
      // Unmute when the user adjusts volume (user gesture)
      videoRef.current.muted = false;
      setIsMuted(false);
      setVolumeLevel(level);
    }
  };

  const toggleMute = () => {
    if (!videoRef.current) return;
    const newMuted = !videoRef.current.muted;
    console.log('[VideoPlayer] toggleMute: newMuted=', newMuted, 'isPlaying=', isPlaying);
    videoRef.current.muted = newMuted;
    setIsMuted(newMuted);
    // When unmuting, hide the poster/play button overlay — the video
    // is now playing with sound (regular playback).
    if (!newMuted) {
      setShowPoster(false);
      setHasPlayedWithSound(true);
    }
  };

  const toggleFullscreen = async () => {
    if (!containerRef.current) return;

    try {
      if (!document.fullscreenElement) {
        await containerRef.current.requestFullscreen();
        setIsFullscreen(true);
      } else {
        await document.exitFullscreen();
        setIsFullscreen(false);
      }
    } catch (err) {
      console.error('Fullscreen error:', err);
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    const time = parseFloat(e.target.value);
    if (videoRef.current) {
      videoRef.current.currentTime = time;
      setCurrentTime(time);
    }
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleVideoError = (e: React.SyntheticEvent<HTMLVideoElement, Event>) => {
    const videoElement = e.currentTarget;
    console.error('[VideoPlayer] Video error:', {
      code: videoElement.error?.code,
      message: videoElement.error?.message,
      src: videoElement.src,
      networkState: videoElement.networkState,
      readyState: videoElement.readyState,
    });
    
    setIsLoading(false);
    setIsPlaying(false);
    
    const errorMessage = videoElement.error ? 
      getVideoErrorMessage({ 
        code: videoElement.error.code,
        message: videoElement.error.message
      }) : 
      'Failed to load video. Please check the URL and try again.';
    
    setError(errorMessage);

    // Call the onError callback if provided
    if (onError && videoElement.error) {
      onError(new Error(errorMessage));
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      console.log('[VideoPlayer] onLoadedMetadata:', {
        duration: videoRef.current.duration,
        videoWidth: videoRef.current.videoWidth,
        videoHeight: videoRef.current.videoHeight,
        readyState: videoRef.current.readyState,
        networkState: videoRef.current.networkState,
      });
      setDuration(videoRef.current.duration);
      setIsLoading(false);

      // Seek to a small offset (0.1s) to force iOS to render the first
      // frame as a visible poster. Without this, iOS Safari may show a
      // black rectangle even with preload="auto".
      try {
        videoRef.current.currentTime = 0.1;
      } catch {
        // Ignore — some browsers throw if not seekable yet
      }

      // Detect aspect ratio
      const videoWidth = videoRef.current.videoWidth;
      const videoHeight = videoRef.current.videoHeight;
      setVideoDimensions({ width: videoWidth, height: videoHeight });
      const ratio = videoWidth / videoHeight;

      if (ratio > 1.7) setAspectRatio('16:9');
      else if (ratio < 0.6) setAspectRatio('9:16');
      else if (ratio === 1) setAspectRatio('1:1');
      else setAspectRatio('4:5');
    }
  };

  // Handle photo slideshow
  if (isPhotoPost && imageUrls.length > 0) {
    
    const nextSlide = () => {
      setCurrentSlide((prev) => (prev + 1) % imageUrls.length);
    };

    const prevSlide = () => {
      setCurrentSlide((prev) => (prev - 1 + imageUrls.length) % imageUrls.length);
    };

    return (
      <div
        ref={containerRef}
        className="relative bg-black rounded-lg overflow-hidden"
        style={{ aspectRatio: '9/16', maxHeight: '100%' }}
      >
        {/* Photo Slideshow */}
        <div className="relative w-full h-full">
          <img
            src={imageUrls[currentSlide]}
            alt={`Slide ${currentSlide + 1}`}
            className="w-full h-full object-contain"
          />

          {/* Navigation Arrows */}
          {imageUrls.length > 1 && (
            <>
              <button
                onClick={prevSlide}
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-all z-10"
                aria-label="Previous slide"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
              <button
                onClick={nextSlide}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-all z-10"
                aria-label="Next slide"
              >
                <ChevronRight className="h-6 w-6" />
              </button>

              {/* Slide Indicators */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 z-10">
                {imageUrls.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      index === currentSlide
                        ? 'bg-white w-6'
                        : 'bg-white/50 hover:bg-white/75'
                    }`}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  if (!processedUrl || error) {
    return (
      <div className="relative bg-black rounded-lg overflow-hidden flex items-center justify-center" style={{ aspectRatio: '9/16', maxHeight: '100%' }}>
        <div className="text-white text-center p-4">
          <AlertCircle className="h-12 w-12 mx-auto mb-2" />
          <p className="whitespace-pre-line">{error || 'No video URL provided'}</p>
        </div>
      </div>
    );
  }

  // Map detected aspect ratio to CSS aspect-ratio string. The container
  // matches the video's actual dimensions so object-contain doesn't
  // letterbox, and the player bar sits at the video's real bottom edge.
  const containerAspectRatio = {
    '9:16': '9/16',
    '16:9': '16/9',
    '1:1': '1/1',
    '4:5': '4/5',
  }[aspectRatio] || '9/16';

  return (
    <>
    <div
      ref={containerRef}
      className="relative bg-black rounded-lg overflow-hidden group cursor-pointer"
      style={{ aspectRatio: containerAspectRatio, maxHeight: '100%', maxWidth: '100%' }}
      onClick={handleVideoClick}
    >
      <video
        ref={videoRef}
        src={processedUrl}
        className="w-full h-full object-contain"
        playsInline
        muted={isMuted}
        controls={false}
        preload="auto"
        onTimeUpdate={() => videoRef.current && setCurrentTime(videoRef.current.currentTime)}
        onLoadedMetadata={handleLoadedMetadata}
        onPlaying={() => {
          console.log('[VideoPlayer] onPlaying: muted=', videoRef.current?.muted, 'hasPlayedWithSound=', hasPlayedWithSound);
          setIsLoading(false);
          setError(null);
          // Only hide poster if playing WITH sound (not muted).
          // When muted (autoplay), keep poster + big play button visible
          // so the user can tap to enable sound.
          if (!videoRef.current?.muted) setShowPoster(false);
        }}
        onPlay={() => {
          console.log('[VideoPlayer] onPlay event');
          setIsPlaying(true);
        }}
        onPause={() => {
          console.log('[VideoPlayer] onPause: hasPlayedWithSound=', hasPlayedWithSound);
          setIsPlaying(false);
          // Only show poster overlay on pause if the video has NOT yet
          // played with sound (initial state). After first sound playback,
          // the frozen video frame is shown with the player bar.
          if (!hasPlayedWithSound) setShowPoster(true);
        }}
        onWaiting={() => {
          console.log('[VideoPlayer] onWaiting (buffering)');
          setIsLoading(true);
        }}
        onEnded={() => {
          console.log('[VideoPlayer] onEnded');
          setIsPlaying(false);
          // After playback ends, keep the frozen frame + player bar
          // (no big play button overlay).
        }}
        onError={handleVideoError}
        crossOrigin={isSupabaseStorageUrl(processedUrl) ? 'anonymous' : undefined}
      />

      {isLoading && !showPoster && (
        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 z-20 pointer-events-none">
          <Loader className="h-12 w-12 text-white animate-spin" />
        </div>
      )}

      {/* Poster image + center play button overlay.
          Shown when: paused, ended, OR playing-but-muted (autoplay).
          Hidden when: playing with sound (regular playback).
          iOS Safari does not reliably show <video poster>, so we render a
          real <img> on top as the background behind the play button.
          When no poster URL is available, we use a transparent background
          so the <video> element's first frame (loaded via preload="auto")
          shows through behind the play button. */}
      {showPoster && !error && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            // If playing muted, just unmute (don't restart).
            // If paused, play with sound.
            if (isPlaying && isMuted) {
              toggleMute();
            } else {
              togglePlayPause(e);
            }
          }}
          className={`absolute inset-0 flex items-center justify-center z-20 ${
            poster ? 'bg-black' : 'bg-transparent'
          }`}
          aria-label={isPlaying && isMuted ? 'Unmute and play with sound' : 'Play video'}
        >
          {poster ? (
            <img
              src={poster}
              alt="Video preview"
              className="absolute inset-0 w-full h-full object-cover"
              onError={(e) => {
                // If poster fails to load, hide it so we don't show a
                // broken image icon — the video first frame shows through.
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          ) : null}
          {isLoading ? (
            <div className="relative z-10">
              <Loader className="h-12 w-12 text-white animate-spin" />
            </div>
          ) : (
            <div className="relative z-10 bg-white/90 hover:bg-white rounded-full p-5 shadow-lg transition-transform active:scale-95">
              <Play className="h-10 w-10 text-gray-900 ml-1" fill="currentColor" />
            </div>
          )}
        </button>
      )}

      <SafeZoneOverlay 
        visible={showSafeZone}
        aspectRatio={aspectRatio}
      />

      {/* Bottom controls bar — visible once the video has played with sound
          (whether currently playing or paused). Hidden during initial load
          and muted autoplay (the big center play button handles those).
          Constrained to the video container width via absolute inset-x-0. */}
      <div 
        ref={controlsRef}
        className={`absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent transition-opacity duration-200 z-20 ${
          hasPlayedWithSound ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="absolute bottom-0 left-0 right-0 p-2 sm:p-3 space-y-1.5">
          <div className="flex items-center space-x-1.5 sm:space-x-2">
            <span className="text-white text-xs sm:text-sm tabular-nums">{formatTime(currentTime)}</span>
            <input
              type="range"
              min="0"
              max={duration || 0}
              value={currentTime}
              onChange={handleSeek}
              className="flex-1 min-w-0 h-1 bg-gray-600 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:cursor-pointer hover:[&::-webkit-slider-thumb]:bg-primary-500"
              disabled={!!error}
            />
            <span className="text-white text-xs sm:text-sm tabular-nums">{formatTime(duration)}</span>
          </div>

          <VideoControls
            isPlaying={isPlaying}
            volumeLevel={volumeLevel}
            isFullscreen={isFullscreen}
            showSafeZone={showSafeZone}
            error={error}
            isMuted={isMuted}
            onPlayPause={() => togglePlayPause({ preventDefault: () => {}, stopPropagation: () => {} } as React.MouseEvent)}
            onVolumeChange={handleVolumeChange}
            onFullscreen={toggleFullscreen}
            onToggleSafeZone={() => setShowSafeZone(!showSafeZone)}
            onToggleMute={toggleMute}
          />
        </div>
      </div>
    </div>
    {/* Video info — small text below the player showing resolution,
        aspect ratio, and duration. Only shown after metadata loads. */}
    {videoDimensions && !error && (
      <div className="flex items-center gap-3 mt-1 px-1 text-[10px] sm:text-xs text-gray-400 dark:text-gray-500 font-mono">
        <span>{videoDimensions.width}×{videoDimensions.height}</span>
        <span className="text-gray-600 dark:text-gray-600">·</span>
        <span>{aspectRatio}</span>
        <span className="text-gray-600 dark:text-gray-600">·</span>
        <span>{formatTime(duration)}</span>
      </div>
    )}
    </>
  );
};